import React, { useState, useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { Shield, Github, AlertCircle, CheckCircle } from 'lucide-react';
import { ScanResults } from './components/ScanResults';
import { LandingPage } from './components/LandingPage';
import { ContactPage } from './components/ContactPage';
import { AboutPage } from './components/AboutPage';
import { apiService, handleApiError } from './services/ApiService';
import { ScanFormData, ScanResult, ScanRequest } from './types/index';
import './styles/index.css';

type AppState = 'idle' | 'scanning' | 'results' | 'error';

function App() {
  const [appState, setAppState] = useState<AppState>('idle');
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isApiHealthy, setIsApiHealthy] = useState<boolean | null>(null);

  const handlePaymentRedirect = React.useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment_status');
    const scanId = params.get('scan_id');

    if (paymentStatus === 'success' && scanId) {
      try {
        await apiService.confirmPayment(scanId);

        // Update current scan result if it's the one that was paid for
        if (scanResult && scanResult.id === scanId) {
          setScanResult({
            ...scanResult,
            isPaid: true
          });
        }

        // Clear params without refreshing
        window.history.replaceState({}, '', '/');
        // You could also show a success toast here
      } catch (error) {
        console.error('Failed to confirm payment:', error);
      }
    }
  }, [scanResult]);

  const checkApiHealth = React.useCallback(async () => {
    try {
      await apiService.healthCheck();
      setIsApiHealthy(true);
    } catch (error) {
      console.error('API health check failed:', error);
      setIsApiHealthy(false);
    }
  }, []);

  // Check API health and payment status on mount
  useEffect(() => {
    checkApiHealth();
    handlePaymentRedirect();
  }, [checkApiHealth, handlePaymentRedirect]);

  const handleScanSubmit = async (formData: ScanFormData) => {
    setAppState('scanning');
    setError(null);
    setErrorCode(null);

    try {
      // Convert form data to scan request
      const standards: Array<'WCAG2A' | 'WCAG2AA' | 'WCAG2AAA' | 'Section508'> = [];
      if (formData.wcag2A) standards.push('WCAG2A');
      if (formData.wcag2AA) standards.push('WCAG2AA');
      if (formData.wcag2AAA) standards.push('WCAG2AAA');
      if (formData.section508) standards.push('Section508');

      const scanRequest: ScanRequest = {
        url: formData.url,
        includeImages: formData.includeImages,
        includeDynamicContent: formData.includeDynamicContent,
        standards
      };

      const result = await apiService.scanWebsite(scanRequest);
      setScanResult(result);
      setAppState('results');
    } catch (error: any) {
      const errorInfo = handleApiError(error);
      setError(errorInfo.message);
      setErrorCode(errorInfo.code || null);
      setAppState('error');
    }
  };

  const handleNewScan = () => {
    setAppState('idle');
    setScanResult(null);
    setError(null);
    setErrorCode(null);
  };

  const Header = () => (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/50 shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-3" onClick={handleNewScan}>
            <div className="p-2 bg-primary-100 rounded-lg">
              <Shield className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Accessibility Scanner
              </h1>
              <p className="text-sm text-gray-600">
                WCAG 2.2 & Section 508 Compliance Checker
              </p>
            </div>
          </Link>



          {/* Navigation Links */}
          <nav className="flex items-center space-x-6 mr-6">
            <Link to="/about" className="text-sm font-semibold text-gray-600 hover:text-primary-600 transition-colors">About</Link>
            <Link to="/contact" className="text-sm font-semibold text-gray-600 hover:text-primary-600 transition-colors">Contact</Link>
          </nav>

          {/* API Health */}
          <div className="hidden md:flex items-center space-x-2 border-l border-gray-200 pl-6">
            {isApiHealthy === null ? (
              <div className="flex items-center text-gray-500">
                <div className="loading-spinner h-4 w-4 mr-2" />
                <span className="text-sm">Checking API...</span>
              </div>
            ) : isApiHealthy ? (
              <div className="flex items-center text-success-600">
                <CheckCircle className="w-4 h-4 mr-2" />
                <span className="text-sm">System Online</span>
              </div>
            ) : (
              <div className="flex items-center text-error-600">
                <AlertCircle className="w-4 h-4 mr-2" />
                <span className="text-sm">System Offline</span>
              </div>
            )}
          </div>


        </div>
      </div>
    </header>
  );

  const Footer = () => (
    <footer className="bg-gray-50 border-t border-gray-200 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Accessibility Scanner. All Rights Reserved.
          </div>
          <div className="flex space-x-6 text-sm font-medium">
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-primary-600 transition-colors flex items-center">
              <Github className="w-4 h-4 mr-1" />
              Github
            </a>
          </div>
        </div>
      </div>
    </footer>
  );

  const LoadingScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <div className="loading-spinner h-12 w-12" />
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Scanning Website...
        </h2>
        <p className="text-gray-600">
          Analyzing accessibility and generating detailed report
        </p>
        <div className="mt-4 text-sm text-gray-500">
          This may take 30-60 seconds depending on page complexity
        </div>
      </div>
    </div>
  );

  const ErrorScreen = () => (
    <div className="text-center space-y-4">
      <div className="flex justify-center">
        <div className="p-3 bg-error-100 rounded-full">
          <AlertCircle className="w-8 h-8 text-error-600" />
        </div>
      </div>
      <h2 className="text-xl font-semibold text-gray-900">Scan Failed</h2>
      <p className="text-gray-600 max-w-md mx-auto">{error}</p>

      <div className="mt-6 flex justify-center space-x-4">
        {errorCode === 'SCAN_LIMIT_EXCEEDED' ? (
          <div className="bg-primary-50 border border-primary-100 rounded-xl p-6 max-w-lg">
            <h3 className="text-primary-900 font-bold mb-2">Monthly Limit Reached</h3>
            <p className="text-primary-800 text-sm">
              Each IP address is limited to 5 free scans per month to ensure system stability for everyone.
              Your limit will reset at the start of next month.
            </p>
          </div>
        ) : (
          <button
            onClick={handleNewScan}
            className="btn-primary"
          >
            Try Again
          </button>
        )}
      </div>

    </div>
  );



  const Home = () => (
    <>
      {appState === 'idle' && (
        <LandingPage
          onScanSubmit={handleScanSubmit}
          error={error || undefined}
        />
      )}

      {appState === 'scanning' && <LoadingScreen />}

      {appState === 'results' && scanResult && (
        <ScanResults
          result={scanResult}
          onNewScan={handleNewScan}
        />
      )}

      {appState === 'error' && <ErrorScreen />}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main id="main-content" className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default App;
